import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { filter, map, distinctUntilChanged, shareReplay, Observable, switchMap } from 'rxjs'
import { notNullOrUndefined, isContainerPartial, isEventCardModel } from 'src/app/shared/functions/type-guards'
import { GeoIPResponseItem } from 'src/app/shared/geo-ip/geo-ip.service'
import { SiteSettings } from 'src/app/shared/models/site-settings.model'
import { GeoBlockingService } from 'src/app/singleton-services/geo-blocking.service'
import { PartialsDataService } from 'src/app/singleton-services/partials/partials-data.service'
import { PlatformService } from 'src/app/singleton-services/platform.service'
import { HeroContentModel, LogoContentModel } from '../batman-data.service'
import { EventCardModel, OfferSelectionCardModel } from '@flocasts/experience-service-types'
import { LiveEventHeader } from '../../signup/signup.interface'
import { SiteIds } from 'src/app/shared/models/site.model'

const SiteBackgroundPositionMap: { [siteId: number]: string } = {
  [SiteIds.FLOTRACK]: 'bg-position-top',
  [SiteIds.FLOCHEER]: 'bg-position-left',
  [SiteIds.FLOCOLLEGE]: 'bg-position-left'
}

@Component({
  selector: 'flo-landing-page',
  templateUrl: './landing-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LandingPageComponent {
  constructor(
    private readonly geoBlockingService: GeoBlockingService,
    private readonly partialsDataService: PartialsDataService,
    private readonly platformService: PlatformService
  ) {}

  @Input() offers: OfferSelectionCardModel[]
  @Input() siteSettings: SiteSettings
  @Input() heroContent: HeroContentModel
  @Input() isPlansVariation = true
  @Input() logoContent: LogoContentModel
  @Input() liveEventHeader?: LiveEventHeader
  @Output() offerSelected = new EventEmitter<OfferSelectionCardModel>()
  @Output() signUpButtonClicked = new EventEmitter<void>()
  @Output() loginButtonClicked = new EventEmitter<void>()
  isNavSignupDisplayed = false

  public upNextContent$ = this.partialsDataService.getLiveAndUpcomingEventsPartial().pipe(
    filter(notNullOrUndefined),
    filter(isContainerPartial),
    map(res => res?.data),
    distinctUntilChanged(),
    shareReplay(1)
  )

  /**
   * gets the list live ids from geoblocked events
   */
  public geoBlockedIds$: Observable<number[]> = this.upNextContent$.pipe(
    map(container => container.items as EventCardModel[]),
    map(events => {
      const geoBlockedEvents = events.filter(
        event =>
          isEventCardModel(event) &&
          event.liveEventMetadata?.checkGeoRestriction &&
          event.liveEventMetadata?.liveEventId
      )
      const geoBlockedIds = geoBlockedEvents.map(event => event.liveEventMetadata?.liveEventId)
      const filterOutUndefined = geoBlockedIds.filter(notNullOrUndefined)
      return filterOutUndefined
    }),
    filter(_ => this.platformService.isBrowser),
    switchMap(this.geoBlockingService.getGeoBlockedLiveEvents),
    map((geoIpResponseItem: GeoIPResponseItem[]) => geoIpResponseItem.map(res => res.id)),
    shareReplay(1)
  )

  get heroBackgroundPosition(): string {
    return SiteBackgroundPositionMap[this.siteSettings.site_id]
  }

  handleOfferSelected(offer: OfferSelectionCardModel): void {
    this.offerSelected.emit(offer)
  }

  handleLoginButtonClicked(): void {
    this.loginButtonClicked.emit()
  }

  handleSignUpButtonClicked(): void {
    this.signUpButtonClicked.emit()
  }

  hideSignupButton() {
    this.isNavSignupDisplayed = false
  }

  showNavSignupButton() {
    this.isNavSignupDisplayed = true
  }
}
